import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { FonotekaMusicTag, FonotekaTrack } from '@/api/client';
import { api } from '@/api/client';
import { ContractorCombo } from '@/components/ContractorCombo/ContractorCombo';
import { MUSIC_TAGS_CATALOG_QUERY_KEY } from '@/lib/readersCatalogQueryCache';
import { groupMusicTagsByGroup } from '@/lib/readerTagCatalogGrouping';
import { formatFileDate } from '@/components/ProjectFilesPopover/projectFilesPopoverUtils';
import { formatFileSizeRuOptional } from '@/utils/formatFileSize';
import { getFileTypeLabel } from '@/components/ProjectFilesPopover/projectFilesPopoverUtils';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export type FonotekaTrackMenuProps = {
  track: FonotekaTrack;
  canWrite: boolean;
  canPickAuthor: boolean;
  disabled?: boolean;
  onRename: () => void;
  onDelete: () => void;
  onOpenTagsCatalog: () => void;
  onToggleTag: (tagId: string, on: boolean) => void;
  onSetAuthor: (legalEntityId: string | null) => void;
  tagsSaving?: boolean;
  authorSaving?: boolean;
};

function TrackMenuMeta({ track }: { track: FonotekaTrack }) {
  const ext = getFileTypeLabel(track.file_name);
  const size = formatFileSizeRuOptional(track.file_size);
  const sizeLine = [ext, size].filter(Boolean).join(' · ') || '—';
  const uploader = track.uploaded_by_name?.trim() || '—';
  const uploadedAt = formatFileDate(track.uploaded_at);

  return (
    <div className="px-[0.45rem] py-[0.45rem]">
      <div className="fonoteka-menu__file-name truncate">{track.file_name}</div>
      <dl className="fonoteka-menu__dl">
        <dt>Автор</dt>
        <dd>{track.legal_entity_name?.trim() || '—'}</dd>
        <dt>Загрузил</dt>
        <dd>
          {uploader} · {uploadedAt}
        </dd>
        <dt>Размер</dt>
        <dd>{sizeLine}</dd>
      </dl>
    </div>
  );
}

export function FonotekaTrackMenu({
  track,
  canWrite,
  canPickAuthor,
  disabled = false,
  onRename,
  onDelete,
  onOpenTagsCatalog,
  onToggleTag,
  onSetAuthor,
  tagsSaving = false,
  authorSaving = false,
}: FonotekaTrackMenuProps) {
  const tagsQuery = useQuery({
    queryKey: MUSIC_TAGS_CATALOG_QUERY_KEY,
    queryFn: () => api.musicTags.list(),
    placeholderData: keepPreviousData,
  });

  const assigned = useMemo(() => new Set(track.tags.map((t) => t.id)), [track.tags]);
  const grouped = useMemo(
    () => groupMusicTagsByGroup(tagsQuery.data ?? []),
    [tagsQuery.data],
  );

  const handleDownload = useCallback(() => {
    const a = document.createElement('a');
    a.href = track.file_url;
    a.download = track.file_name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [track.file_name, track.file_url]);

  const authorValue =
    track.legal_entity_id && track.legal_entity_name
      ? { id: track.legal_entity_id, name: track.legal_entity_name }
      : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="fonoteka-icon-btn justify-self-end"
          aria-label="Настройки трека"
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
        >
          ⋮
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuItem disabled={!canWrite} onSelect={() => onRename()}>
          Переименовать
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={!canWrite || tagsSaving}>Теги</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-56">
            {tagsQuery.isLoading && (tagsQuery.data ?? []).length === 0 ? (
              <DropdownMenuLabel className="font-normal text-muted-foreground">
                Загрузка…
              </DropdownMenuLabel>
            ) : grouped.length === 0 ? (
              <DropdownMenuLabel className="font-normal text-muted-foreground">
                Тегов пока нет
              </DropdownMenuLabel>
            ) : (
              grouped.map(({ group, tags }) => (
                <div key={group}>
                  <DropdownMenuLabel className="text-xs text-muted-foreground">{group}</DropdownMenuLabel>
                  {tags.map((tag: FonotekaMusicTag) => (
                    <DropdownMenuCheckboxItem
                      key={tag.id}
                      checked={assigned.has(tag.id)}
                      disabled={!canWrite || tagsSaving}
                      onCheckedChange={(checked) => onToggleTag(tag.id, checked === true)}
                      onSelect={(e) => e.preventDefault()}
                    >
                      {tag.name}
                    </DropdownMenuCheckboxItem>
                  ))}
                </div>
              ))
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onOpenTagsCatalog()}>Редактировать справочник</DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger disabled={!canWrite || !canPickAuthor || authorSaving}>
            Автор
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-72" onClick={(e) => e.stopPropagation()}>
            {canPickAuthor ? (
              <ContractorCombo
                value={authorValue}
                onChange={(next) => onSetAuthor(next?.id ?? null)}
                source="legalEntity"
                placeholder="Юрлицо"
                disabled={!canWrite || authorSaving}
                fullWidth
              />
            ) : (
              <p className="px-2 py-1 text-xs text-muted-foreground">
                {track.legal_entity_name?.trim() || '—'}
              </p>
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuItem onSelect={() => handleDownload()}>Скачать</DropdownMenuItem>
        <DropdownMenuItem
          disabled={!canWrite}
          variant="destructive"
          onSelect={() => onDelete()}
        >
          Удалить
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <TrackMenuMeta track={track} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
